import { ViewMode } from "@/store/zustand/communityPanelStore";
import {
  getAllCategories,
  getSubcategoriesByCategory,
  getItemsBySubcategory,
  getAllItems,
} from "@/utils/headingsToTagsMap";
import { ChevronDown, ChevronRight } from "lucide-react";
import cn from "classnames";
import React from "react";
import { BaseCard } from "./CommunityCard";
import { TagListItem } from "./TagListItem";

export interface SubcategoryItemProps {
  category: string;
  subcategory: string;
  items: { displayName: string; id: string; url: string | null }[];
  setSelectedCategory: (categoryId: string | null) => void;
  selectedCategory: string | null;
  tagCounts: Record<string, number>;
}

const SubcategoryItem: React.FC<SubcategoryItemProps> = ({
  subcategory,
  items,
  setSelectedCategory,
  selectedCategory,
  tagCounts,
}) => (
  <div className="pt-2 mb-2 select-none">
    <h4 className="text-2xs text-text-secondary mb-1 px-2">{subcategory}</h4>
    <div className="flex flex-col gap-1">
      {items.map((item) => (
        <TagListItem
          key={item.id}
          item={item}
          isSelected={selectedCategory === item.id}
          onSelect={() => setSelectedCategory(item.id)}
          count={tagCounts[item.id] || 0}
        />
      ))}
    </div>
  </div>
);

export const CategoryView = React.memo(function CategoryView({
  viewMode,
  toggleCategoryExpanded,
  expandedCategories,
  setSelectedCategory,
  selectedCategory,
  componentCounts,
  handleFilterChange,
  leftPanelFilter,
}: {
  viewMode: ViewMode;
  toggleCategoryExpanded: (category: string) => void;
  expandedCategories: Record<string, boolean>;
  setSelectedCategory: (categoryId: string | null) => void;
  selectedCategory: string | null;
  componentCounts: {
    collections: Record<string, number>;
    tags: Record<string, number>;
  };
  handleFilterChange: () => void;
  leftPanelFilter: string;
}): React.ReactNode {
  const lowerCaseFilter = leftPanelFilter.toLowerCase();

  const filterItems = (
    items: { displayName: string; id: string; url: string | null }[]
  ) => {
    if (!leftPanelFilter) {
      return items;
    }
    return items.filter((item) =>
      item.displayName.toLowerCase().includes(lowerCaseFilter)
    );
  };

  const renderCategoryHeader = (category: string) => (
    <div
      className="p-4 font-semibold text-xs text-secondary-n-50 bg-secondary-0 hover:bg-background-tertiary cursor-pointer flex justify-between items-center select-none transition-all"
      onClick={() => toggleCategoryExpanded(category)}
    >
      <span>{category}</span>
      <div className="transition-transform ">
        {expandedCategories[category] ? (
          <ChevronDown className="w-4 h-4 text-icons-secondary transform rotate-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-icons-secondary transform rotate-0" />
        )}
      </div>
    </div>
  );

  const renderCollapsibleContent = (
    category: string,
    children: React.ReactNode
  ) => (
    <div
      className={cn(
        "overflow-hidden transition-all ease-in-out",
        expandedCategories[category]
          ? "opacity-100 max-h-[10000px]" // fixed max height to get animation working
          : "max-h-0 opacity-0"
      )}
    >
      {children}
    </div>
  );

  return (
    <div className="flex flex-col divide-y divide-secondary-300 w-full overflow-y-scroll scrollbar-hide">
      {getAllCategories().map((category) => {
        // Determine if any subcategory within this category has items after filtering
        const hasVisibleItems = getSubcategoriesByCategory(category).some(
          (subcategory) => {
            const items = getItemsBySubcategory(category, subcategory);
            return filterItems(items).length > 0;
          }
        );

        // Only render the category if it has items after filtering OR if no filter is active
        if (!hasVisibleItems && leftPanelFilter) {
          return null;
        }

        return (
          <div key={category} className="text-xs flex flex-col">
            {renderCategoryHeader(category)}
            {renderCollapsibleContent(
              category,
              viewMode === ViewMode.Grid ? (
                <div className="ml-4 mr-4 border-l pl-2 mb-4">
                  {getSubcategoriesByCategory(category).map((subcategory) => {
                    const items = getItemsBySubcategory(category, subcategory);
                    const filteredGridSubItems = filterItems(items);

                    // Only render subcategory if it has items after filtering
                    if (filteredGridSubItems.length === 0) {
                      return null;
                    }

                    return (
                      <div
                        key={`${category}-${subcategory}-grid`}
                        className="pt-2 mb-2 select-none"
                      >
                        <h4 className="text-2xs text-text-secondary mb-2 px-2">
                          {subcategory}
                        </h4>
                        <div className="grid grid-cols-[repeat(auto-fill,minmax(100px,1fr))] gap-2 auto-rows-min w-full">
                          {filteredGridSubItems.map((item) => (
                            <BaseCard
                              key={item.id}
                              title={item.displayName}
                              count={componentCounts.tags[item.id] || 0}
                              imageUrl={item.url || undefined}
                              selected={selectedCategory === item.id}
                              onClick={() => {
                                setSelectedCategory(
                                  selectedCategory === item.id ? null : item.id
                                );
                                handleFilterChange();
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="ml-4 mr-4 border-l pl-2 mb-4">
                  {getSubcategoriesByCategory(category).map((subcategory) => {
                    const items = getItemsBySubcategory(category, subcategory);
                    const filteredListSubItems = filterItems(items);

                    // Only render subcategory if it has items after filtering
                    if (filteredListSubItems.length === 0) {
                      return null;
                    }

                    return (
                      <SubcategoryItem
                        key={`${category}-${subcategory}`}
                        category={category}
                        subcategory={subcategory}
                        items={filteredListSubItems}
                        setSelectedCategory={setSelectedCategory}
                        selectedCategory={selectedCategory}
                        tagCounts={componentCounts.tags}
                      />
                    );
                  })}
                </div>
              )
            )}
          </div>
        );
      })}
    </div>
  );
});

export default CategoryView;
